import type { TranslationSchema } from '../pt-BR';
import type { DeepPartialStrings } from '../types';

export const installments = {
    layout: {
        title: 'Installments',
        filtersTitle: 'Filters',
        simulateTitle: 'Can I fit one more?',
    },
    summary: {
        perMonth: 'Per month',
        inMonth: 'In {{month}}',
        monthBarAccessibility: '{{month}}, {{amount}} in installments',
        allSources: 'All cards and accounts',
        incomeShare: '{{share}} of income',
        remaining: 'Left to pay',
        freeOf: 'Installment-free',
        alreadyFree: 'already',
    },
    list: {
        count_zero: 'No installment plans',
        count_one: '{{count}} installment plan',
        count_other: '{{count}} installment plans',
        sortedBy: 'Sorted: {{sort}}',
        perMonth: 'per month',
        enteredIn: 'Bought in {{month}}',
        startsIn: 'Starts in {{month}}',
        endsIn: 'ends in {{month}}',
        paidOffIn: 'paid off in {{month}}',
        remaining: 'left',
        total: 'total',
        badges: {
            done: 'Paid off',
            last: 'Last one',
            new: 'New',
        },
        filterEmptyTitle: 'Nothing matches these filters',
        filterEmptySubtitle: 'Try another card or a longer period.',
        clearFilters: 'Clear filters',
        monthEmptyTitle: 'No installments in {{month}}',
        allMonths: 'See all months',
        emptyTitle: 'No installment purchases',
        emptySubtitle: 'When you split a purchase on a card or an account, the installments show up here, added up month by month.',
        simulate: 'Simulate a new purchase',
        filtersAccessibility: 'Filters',
        removeFilterAccessibility: 'Remove filter: {{label}}',
        hiddenAmount: '$ ••••',
    },
    filters: {
        card: 'Card',
        allCards: 'All cards',
        account: 'Account',
        allAccounts: 'All accounts',
        period: 'Bought',
        status: 'Status',
        sort: 'Sort',
        sortTitle: 'Sort by',
        clear: 'Clear',
        purchases_one: '{{count}} purchase',
        purchases_other: '{{count}} purchases',
        viewCount_one: 'View {{count}} plan',
        viewCount_other: 'View {{count}} plans',
        noResults: 'No results',
        periodTag: 'Bought: {{period}}',
        monthTag: 'Installments in {{month}}',
        periods: {
            all: 'Any time',
            thisMonth: 'This month',
            last3: 'Last 3 months',
            last6: 'Last 6 months',
            last12: 'Last 12 months',
        },
        periodScope: {
            thisMonth: 'bought this month',
            last3: 'last 3 months',
            last6: 'last 6 months',
            last12: 'last 12 months',
        },
        statuses: {
            open: 'In progress',
            last: 'Last installment',
            done: 'Paid off',
            all: 'All',
        },
        sorts: {
            endsSoon: 'Ends soonest',
            recent: 'Most recent',
            largest: 'Largest installment',
            remaining: 'Left to pay',
        },
    },
    simulate: {
        amountLabel: 'Purchase amount',
        card: 'Card',
        onAccount: 'On an account',
        firstInstallment: '1st installment',
        firstInvoice: '{{month}} statement',
        installments: 'Installments',
        optionAccessibility: '{{count}}x of {{amount}}',
        chartTitle: 'Installments per month',
        legendCurrent: 'Current',
        legendNew: 'New',
        limitLabel: '{{percent}} of income',
        chartNote: 'Only installments count here, from cards and accounts; other expenses do not. The line is {{percent}} of your average income from {{from}} to {{to}}.',
        chartNoteNoIncome: 'Only installments count here, from cards and accounts; other expenses do not.',
        and: 'and',
        typeAmountTitle: 'How much is it?',
        typeAmountBody: 'Enter the amount to see how it stacks on top of the installments you already have.',
        planLine: '{{count}}x of {{amount}} until {{month}}.',
        whenList: 'in {{months}}',
        whenRange: 'from {{from}} to {{to}}',
        whenFrom: 'from {{from}} on',
        redTitle: 'Goes into the red {{when}}',
        redSuggestion: 'In {{count}}x of {{amount}} the projected balance stays positive.',
        deeperTitle: 'Your projected balance is already negative {{when}}',
        deeperBody: 'The purchase pushes those months further into the red.',
        overTitle: 'Stays out of the red, but goes over {{percent}} of income {{when}}',
        incomeOverTitle: 'Goes over {{percent}} of income {{when}}',
        overBody: 'Installments peak at {{peak}} in {{peakMonth}}.',
        overSuggestion: 'In {{count}}x of {{amount}} it stays within {{percent}}.',
        noSuggestion: 'Splitting it into more installments, up to 24x, does not help.',
        fitsTitle: 'Fits your balance and your income',
        fitsBalanceTitle: 'Fits your balance',
        incomeFitsTitle: 'It fits: no month with the new installment goes over {{percent}} of income',
        noIncomeNote: 'With no income in the last 3 months, there is nothing to compare against your income.',
        alreadyOver: 'In {{months}}, the installments you already have are over the line on their own.',
        balanceLabel: 'Projected balance in {{month}}',
        balanceLowestLabel: 'Projected balance in the tightest month, {{month}}',
        balanceNote: 'Projected balance: what is available today plus scheduled bills, recurring items, statements and your average day-to-day spending.',
        trySuggestion: 'Try {{count}}x',
        noIncomeTitle: 'With it, the heaviest month is {{peak}}',
        noIncomeBody: 'With no income logged in the last 3 months, there is nothing to compare against yet. {{count}}x of {{amount}} until {{month}}.',
        chartAccessibility: 'Chart of the next 12 months of installments with the simulated purchase',
    },
    teaser: {
        title: 'All your installments in one place',
        subtitle: 'How much is left, when your statement gets lighter and whether one more purchase fits, across your cards and accounts.',
        cta: 'Unlock Installments',
    },
} satisfies DeepPartialStrings<TranslationSchema['installments']>;
